import { Dialog, Icon, SelectionBox } from '@infotech-soe/erp-ui-kit';
import {
  TPivotPresetName,
  TPivotReport,
} from '@infotech-soe/merps/types/Reports';
import { tg } from '@infotech-soe/metaschema';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Pivot } from 'react-flexmonster';
import { useIntl } from 'react-intl';

import useRuntimeConfig from '../../hooks/useRuntimeConfig';
import { useStyles as useDialogStyles } from '../dialogs/styles';

import { messages } from './messages';
import PivotReport from './PivotReport';
import type { PivotReportDialogProps } from './PivotReportDialogContainer';
import PivotSavePresetForm from './PivotSavePresetForm';
import PivotSelectPresetForm from './PivotSelectPresetForm';

const PivotReportDialog = (
  props: PivotReportDialogProps
): React.JSX.Element => {
  const dialogClasses = useDialogStyles();
  const {
    dataSource,
    config,
    code,
    presetNames,
    show,
    isDarkMode,
    onClose,
    onError,
    onSavePreset,
    onSelectPreset,
  } = props;
  const { formatMessage } = useIntl();

  const [isOpenedPresetSaveForm, setIsOpenedPresetSaveForm] = useState(false);
  const [isOpenedPresetSelectForm, setIsOpenedPresetSelectForm] =
    useState(false);
  const [isReportChanged, setIsReportChanged] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isOrientationSelectorOpen, setIsOrientationSelectorOpen] =
    useState(false);
  const [isClosed, setIsClosed] = useState(false);

  const pivotRef = useRef<Pivot | null>(null);

  const title = useMemo(
    () => [code, config?.currentPreset].filter(tg.isSomething).join(': '),
    [code, config?.currentPreset]
  );

  const handleOpenPresetSelectForm = useCallback(() => {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    }
    setIsOpenedPresetSelectForm(true);
  }, []);

  const handleSelectPreset = useCallback(
    (preset?: TPivotPresetName) => {
      onSelectPreset(preset);
      setIsOpenedPresetSelectForm(false);
    },
    [onSelectPreset]
  );

  const handleOpenPresetSaveForm = useCallback(() => {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    }
    setIsOpenedPresetSaveForm(true);
  }, []);

  const handleSavePreset = (presetName?: TPivotPresetName): void => {
    if (tg.isSomething(presetName)) {
      const pivotReport =
        pivotRef.current?.flexmonster.getReport() as TPivotReport;
      onSavePreset(presetName, pivotReport, isClosed);
    }
    setIsOpenedPresetSaveForm(false);
    setIsReportChanged(false);
    setIsClosed(false);
  };

  const handleClose = useCallback(() => {
    if (
      isOpenedPresetSaveForm ||
      isOpenedPresetSelectForm ||
      isOrientationSelectorOpen ||
      showConfirm
    ) {
      return;
    }
    if (isReportChanged) {
      setShowConfirm(true);
      return;
    }
    onClose();
  }, [
    isOpenedPresetSaveForm,
    isOpenedPresetSelectForm,
    isOrientationSelectorOpen,
    isReportChanged,
    onClose,
    showConfirm,
  ]);

  const licenseKey = useRuntimeConfig(onError)?.pivotLicenseKey;

  useEffect(() => {
    pivotRef.current?.flexmonster.setReport({
      dataSource,
      ...config?.presets[config.currentPreset],
    });
  }, [config, dataSource, pivotRef]);

  const handleConfirmCancel = () => {
    setShowConfirm(false);
  };

  const handleConfirmSubmit = (isSave: boolean) => {
    setShowConfirm(false);
    if (isSave) {
      setIsClosed(true);
      setIsOpenedPresetSaveForm(true);
    } else {
      setIsReportChanged(false);
      onClose();
    }
  };

  return (
    <Dialog
      open={!(!show || !licenseKey)}
      title={title}
      height="95vw"
      width="95vw"
      noChildrenWrap
      onClose={handleClose}
    >
      <PivotReport
        currentPreset={config?.currentPreset}
        licenseKey={licenseKey ?? ''}
        presetNames={presetNames}
        reportRef={pivotRef}
        show={show}
        isDarkMode={isDarkMode}
        onOpenPreset={handleOpenPresetSelectForm}
        onSavePreset={handleOpenPresetSaveForm}
        onReportChange={() => setIsReportChanged(true)}
        onOrientationSelectorChange={setIsOrientationSelectorOpen}
      />
      {isOpenedPresetSaveForm && (
        <PivotSavePresetForm
          currentPreset={config?.currentPreset}
          open={isOpenedPresetSaveForm}
          presetNames={presetNames}
          onCancel={() => setIsOpenedPresetSaveForm(false)}
          onSavePreset={handleSavePreset}
        />
      )}
      {isOpenedPresetSelectForm && (
        <PivotSelectPresetForm
          currentPreset={config?.currentPreset}
          open={isOpenedPresetSelectForm}
          presetNames={presetNames}
          onCancel={() => setIsOpenedPresetSelectForm(false)}
          onConfirm={handleSelectPreset}
        />
      )}
      <SelectionBox
        className={dialogClasses.dialog}
        height="auto"
        open={showConfirm}
        title={title}
        width="auto"
        onAccept={() => handleConfirmSubmit(true)}
        onDecline={() => handleConfirmSubmit(false)}
        onCancel={handleConfirmCancel}
        onClose={handleConfirmCancel}
      >
        <div className={dialogClasses.container}>
          <div className={dialogClasses.header}>
            <div className={dialogClasses.icon}>
              <Icon name="question" size="3em" />
            </div>
            <div className={dialogClasses.shortMessage}>
              {formatMessage(messages.savePreset) + '?'}
            </div>
          </div>
        </div>
      </SelectionBox>
    </Dialog>
  );
};

export default memo(PivotReportDialog);
